import React from "react";
import { X, Printer } from "lucide-react";

const STATUS = {
  ok: { c: "#059669", t: "IN RANGE" },
  low: { c: "#D97706", t: "LOW" },
  high: { c: "#DC2626", t: "HIGH" },
};

export default function Report({ branding, client, test, onClose }) {
  const b = branding || {};
  const when = new Date(test.created_at).toLocaleString();
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 flex items-start justify-center overflow-y-auto p-4" data-testid="report-modal">
      <style>{`@media print { body * { visibility: hidden !important; } #report-print, #report-print * { visibility: visible !important; } #report-print { position: absolute; left: 0; top: 0; width: 100%; box-shadow: none !important; } .no-print { display: none !important; } }`}</style>
      <div className="w-full max-w-2xl bg-white rounded-2xl shadow-2xl my-6">
        <div className="no-print flex justify-between items-center p-4 border-b border-slate-100">
          <span className="font-display font-bold text-slate-900">Water Test Report</span>
          <div className="flex gap-2">
            <button onClick={() => window.print()} data-testid="report-print-btn" className="inline-flex items-center gap-2 rounded-full bg-slate-900 hover:bg-sky-700 text-white text-sm font-semibold px-4 py-2 transition-colors"><Printer className="h-4 w-4" /> Print</button>
            <button onClick={onClose} data-testid="report-close-btn" className="rounded-full border border-slate-200 p-2 hover:bg-slate-50"><X className="h-4 w-4" /></button>
          </div>
        </div>

        <div id="report-print" className="p-8">
          {/* Branded header */}
          <div className="flex items-center justify-between border-b-4 pb-4" style={{ borderColor: b.colour || "#0891B2" }}>
            <div className="flex items-center gap-3">
              {b.logo_url ? <img src={b.logo_url} alt="logo" className="h-12 w-12 object-contain" /> : <div className="h-12 w-12 rounded-xl flex items-center justify-center text-white font-display font-bold text-xl" style={{ background: b.colour || "#0891B2" }}>{(b.company || "A")[0]}</div>}
              <div>
                <p className="font-display font-extrabold text-xl text-slate-900">{b.company || "Aqualux Nexus"}</p>
                <p className="text-xs text-slate-500">{b.tagline}</p>
              </div>
            </div>
            <div className="text-right text-xs text-slate-500">
              {b.phone && <p>{b.phone}</p>}{b.email && <p>{b.email}</p>}{b.address && <p>{b.address}</p>}
            </div>
          </div>

          {/* client + meta */}
          <div className="flex justify-between mt-5 text-sm">
            <div>
              <p className="font-mono2 text-[11px] uppercase tracking-widest text-slate-400">Client</p>
              <p className="font-bold text-slate-900">{client.name}</p>
              <p className="text-slate-500">{client.email} {client.phone && `· ${client.phone}`}</p>
              <p className="text-slate-500 capitalize">{client.pool?.type} · {client.pool?.volume_litres?.toLocaleString()} L · {client.pool?.sanitiser}</p>
            </div>
            <div className="text-right">
              <p className="font-mono2 text-[11px] uppercase tracking-widest text-slate-400">Test</p>
              <p className="text-slate-700">{when}</p>
              <p className="text-slate-500 capitalize">Source: {test.source}</p>
              <p className="text-slate-500">By: {test.operator || "—"}</p>
            </div>
          </div>

          {/* readings table */}
          <table className="w-full mt-6 text-sm border-collapse">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-widest text-slate-400 border-b border-slate-200">
                <th className="py-2">Parameter</th><th className="py-2">Result</th><th className="py-2">Ideal</th><th className="py-2 text-right">Status</th>
              </tr>
            </thead>
            <tbody>
              {(test.summary || []).map((r) => {
                const s = STATUS[r.status] || STATUS.ok;
                return (
                  <tr key={r.key} className="border-b border-slate-100">
                    <td className="py-2.5 font-medium text-slate-800">{r.label}</td>
                    <td className="py-2.5 font-mono2 text-slate-900">{r.value} {r.unit}</td>
                    <td className="py-2.5 font-mono2 text-slate-500">{r.ideal} {r.unit}</td>
                    <td className="py-2.5 text-right"><span className="font-mono2 text-[11px] font-bold" style={{ color: s.c }}>{s.t}</span></td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {test.notes && <p className="mt-4 text-sm text-slate-600"><span className="font-semibold">Notes:</span> {test.notes}</p>}
          <p className="mt-8 pt-4 border-t border-slate-100 text-[11px] text-slate-400">Generated by Aqualux Nexus Pool Software · {b.company || "Aqualux Nexus"}. Results are a guide; retest after any chemical adjustment.</p>
        </div>
      </div>
    </div>
  );
}
